import api from './api';
import { filenameFromDisposition } from '@/shared/utils/downloadBlob';

/**
 * Service for Course Support Applications
 * One application per course with ranked program preferences
 */
export const applicationService = {
    /**
     * Get all applications for current user (as applicant)
     */
    async getMyApplications() {
        const response = await api.get('/application/mine');
        return response.data;
    },

    /**
     * Get application by ID
     */
    async getApplicationById(applicationId) {
        const response = await api.get(`/application/${applicationId}`);
        return response.data;
    },

    /**
     * Create a new course support application
     * @param {Object} applicationData - Application data including:
     *   - courseId: The course being applied for
     *   - term: Academic term code (e.g., '202610')
     *   - programPreferences: Array of {programType, rank} objects
     *   - qualifications: Relevant qualifications
     *   - availability: Schedule availability
     *   - instructorOfRecord: User ID if submitting on behalf of another instructor
     *   - isSubmittedOnBehalf: Boolean flag for on-behalf submissions
     *   - namedStudents: Array of {name, email, studentId, forProgram} (required for TA)
     *   - courseConfirmation: {sectionNumber, instructorOfRecordName, instructorOfRecordEmail, modality, otherModality, meetings: [{type, otherType, days, otherDays, time, otherTime, room}]}
     *   - courseContext: {rationaleForSupport, supportUseCases, otherSupportUseCase, peerSupporterBenefit, additionalNotes}
     *   - programSpecificResponses: {ta: {...}, la: {...}, si: {...}} conditional on selected programs
     *   - acknowledgesGeneralTerms: Boolean, required before submit (applies regardless of selected programs)
     *   - confirmsSubmissionAccuracy: Boolean, required before submit (confirms the Review and Submit summary is accurate)
     */
    async createApplication(applicationData) {
        const response = await api.post('/application', applicationData);
        return response.data;
    },

    /**
     * Update an existing application (only if still editable)
     */
    async updateApplication(applicationId, applicationData) {
        const response = await api.put(
            `/application/${applicationId}`,
            applicationData
        );
        return response.data;
    },

    /**
     * Submit a draft application (transitions status from 'draft' to 'submitted')
     * The server refuses it unless every ranked program is accepting
     * applications from the caller, naming each program that is not.
     */
    async submitApplication(applicationId) {
        const response = await api.post(`/application/${applicationId}/submit`);
        return response.data;
    },

    /**
     * Delete a draft the applicant no longer wants. The server refuses
     * anything already submitted (withdraw that instead).
     */
    async deleteApplication(applicationId) {
        await api.delete(`/application/${applicationId}`);
    },

    /**
     * Withdraw an application
     */
    async withdrawApplication(applicationId) {
        const response = await api.post(
            `/application/${applicationId}/withdraw`
        );
        return response.data;
    },

    /**
     * Replace the LA nominees on a submitted application, without reopening
     * the form. Answers { application, nominations, notice }: nominations
     * and notice are null until LA's decision is released to the caller.
     * @param {Array<{name: string, email: string, studentId: string}>} nominees
     */
    async updateNominees(applicationId, nominees) {
        const response = await api.put(
            `/application/${applicationId}/nominees`,
            { nominees }
        );
        return response.data;
    },

    /**
     * A coordinator's edit of a submitted request, applied in place. Send only
     * the fields that changed: { courseConfirmation?, programSpecificResponses? }.
     * Answers { application, changed }, `changed` naming what the save changed.
     */
    async coordinatorEdit(applicationId, changes) {
        const response = await api.put(
            `/application/${applicationId}/coordinator-edit`,
            changes
        );
        return response.data;
    },

    /**
     * Add or change the instructor of record: `{ name, email }`, or
     * `{ userId }` for an account. Answers { application, changed, named,
     * removed }, the last two saying whether the new instructor and the
     * replaced one were emailed.
     */
    async changeInstructor(applicationId, instructor) {
        const response = await api.put(
            `/application/${applicationId}/instructor`,
            instructor
        );
        return response.data;
    },

    /**
     * Who a change to the meeting times or the LA count reaches, for the
     * coordinator's own programs: { programs: [{ program, accepted, offers,
     * applicants }] }, each a list of names
     */
    async coordinatorEditImpact(applicationId) {
        const response = await api.get(
            `/application/${applicationId}/coordinator-edit/impact`
        );
        return response.data;
    },

    /**
     * The submitter changes the section's meeting days, times and room after
     * submit. Answers { application, changed, timesChanged }.
     */
    async updateMeetings(applicationId, meetings) {
        const response = await api.put(
            `/application/${applicationId}/meetings`,
            { courseConfirmation: { meetings } }
        );
        return response.data;
    },

    /**
     * How many placed students hear of a change to the days or times, among
     * the programs whose decisions the caller may see: { placed }
     */
    async meetingsImpact(applicationId) {
        const response = await api.get(
            `/application/${applicationId}/meetings/impact`
        );
        return response.data;
    },

    /**
     * Start a revision of your own submitted request, before any program has
     * decided it
     */
    async reviseApplication(applicationId) {
        const response = await api.post(`/application/${applicationId}/revise`);
        return response.data;
    },

    /**
     * Reopen your own withdrawn request for revision. The response carries
     * `reopened: { crnRestored, formerCrn }`.
     */
    async reopenApplication(applicationId) {
        const response = await api.post(`/application/${applicationId}/reopen`);
        return response.data;
    },

    /**
     * Whether an application already exists for a CRN in a term — the live
     * check Section 1 runs while someone types the CRN. Returns
     * { exists: false } or { exists: true, own, id?, status? } — id/status
     * are only present when it's the caller's own application. excludeId is
     * the draft being edited, so it doesn't report itself.
     */
    async checkCrn(term, crn, excludeId) {
        const response = await api.get('/application/crn-check', {
            params: { term, crn, ...(excludeId && { exclude: excludeId }) },
        });
        return response.data;
    },

    /**
     * Get applications for a program (coordinator view)
     * @param {string} programType - 'si', 'ta', or 'la'
     * @param {Object} filters - Optional filters: term, status
     */
    async getApplicationsByProgram(programType, filters = {}) {
        const response = await api.get(`/application/program/${programType}`, {
            params: filters,
        });
        // Callers get the array. If the server's whole-term backstop cut
        // the list off, the array also carries truncated and the real total
        // so the dashboards can say so. Those are plain properties, so read
        // them off this array directly: a copy (filter, map, spread) drops them
        const { applications, truncated, total } = response.data;
        if (truncated) Object.assign(applications, { truncated, total });
        return applications;
    },

    /**
     * The requests faculty have started but not submitted for a program and
     * term, as a coordinator may see them: course, section, the faculty
     * member's name and email, when it was last saved and whether every
     * section is filled in. Never the answers.
     * @param {string} programType - 'si', 'ta', or 'la'
     * @param {string} term - Term code
     */
    async getProgramDrafts(programType, term) {
        const response = await api.get(
            `/application/program/${programType}/drafts`,
            { params: { term } }
        );
        return response.data.drafts;
    },

    /**
     * Every application for a program and term as a CSV file
     * @param {string} programType - 'si', 'ta', or 'la'
     * @param {string} term - Term code
     * @returns {{ blob: Blob, filename: string|null }} filename is the
     *   server's, or null when the browser can't read it
     */
    async exportApplicationsByProgram(programType, term) {
        const response = await api.get(
            `/application/program/${programType}/export`,
            { params: { term }, responseType: 'blob' }
        );
        return {
            blob: response.data,
            filename: filenameFromDisposition(
                response.headers?.['content-disposition']
            ),
        };
    },

    /**
     * Request revision on an application (coordinator only)
     * @param {string} applicationId - The application ID
     * @param {string} revisionNotes - Notes explaining what needs revision
     * @param {{ waitlist?: 'keep' | 'remove' }} [options] - For an application
     *   the program has waitlisted: whether it stays on the waitlist
     */
    async requestRevision(applicationId, revisionNotes, options = {}) {
        const response = await api.post(
            `/application/${applicationId}/request-revision`,
            {
                revisionNotes,
                ...(options.waitlist && { waitlist: options.waitlist }),
            }
        );
        return response.data;
    },
};

export default applicationService;
